import { useCallback, useMemo } from 'react'

import { PermissionsService, TeamApiService, type TeamMemberDto } from '@api'
import { useUnit } from 'effector-react'
import { useQuery } from 'react-query'

import { $creators, $isCreatorProcessCompleted } from '@/models/creators.ts'

import type {
  TeamMemberPermissionsResponse,
  TeamMemberPermissionsTransformed,
} from '../types'

export const useTeamMember = (id: string) => {
  const creators = useUnit($creators)
  const isCreatorProcessCompleted = useUnit($isCreatorProcessCompleted)

  const mapCreatorIdToCreatorName = useCallback(
    (data: TeamMemberPermissionsResponse) => {
      const permissions = data.permissions?.map(({ creatorId, ...rest }) => {
        const creator = creators.find((creator) => creator.id === creatorId)

        return {
          creatorPermissions: {
            ...rest,
          },
          creatorId,
          creatorName: creator?.name || 'Unknown Creator',
        }
      })

      return {
        ...data,
        permissions,
      }
    },
    [creators],
  )

  const _fetchTeamMemberData = useCallback(async (): Promise<
    [TeamMemberDto, TeamMemberPermissionsTransformed]
  > => {
    const [memberResponse, permissionsResponse] = await Promise.all([
      TeamApiService.findMember({ id: +id }),
      // TODO: BE need to add creatorId to type
      PermissionsService.createPermissionProfileForTarget({ memberId: +id }),
    ])

    const transformedData = mapCreatorIdToCreatorName(
      permissionsResponse as unknown as TeamMemberPermissionsResponse,
    )

    return [memberResponse, transformedData]
  }, [id, mapCreatorIdToCreatorName])

  const {
    data: [memberData, memberPermissionData] = [],
    isFetching,
    isIdle,
    isSuccess,
    error,
  } = useQuery<[TeamMemberDto, TeamMemberPermissionsTransformed]>(
    // Unique identifier for the query to avoid caching
    ['member', id],
    _fetchTeamMemberData,
    {
      enabled: isCreatorProcessCompleted,
      cacheTime: 0,
    },
  )

  return useMemo(
    () => ({
      data: memberData,
      permissions: memberPermissionData,
      isFetching: isFetching || isIdle,
      isSuccess: isCreatorProcessCompleted && isSuccess,
      error,
    }),
    [
      memberData,
      memberPermissionData,
      isFetching,
      isIdle,
      isCreatorProcessCompleted,
      isSuccess,
      error,
    ],
  )
}
